import type { AnnouncementDismissal } from "replo-utils/lib/inAppAnnouncement";

import { useState } from "react";

import {
  dismissInAppAnnouncement,
  undismissedInAppAnnouncements,
} from "replo-utils/lib/inAppAnnouncement";

export function useInAppAnnouncementSession<
  Announcement extends AnnouncementDismissal,
>({
  announcements,
  userId,
  replay,
}: {
  announcements: Announcement[];
  userId: string;
  replay: boolean;
}) {
  // Lazy so a localStorage SecurityError surfaces inside the helpers' try/catch.
  const storage = {
    getItem: (key: string) => window.localStorage.getItem(key),
    setItem: (key: string, value: string) => {
      window.localStorage.setItem(key, value);
    },
  };
  const [session, setSession] = useState(() => ({
    announcements,
    queue: replay
      ? announcements
      : undismissedInAppAnnouncements({ announcements, userId, storage }),
  }));
  // Keep the visible queue stable until the customer closes it.
  if (session.queue.length === 0 && session.announcements !== announcements) {
    const newVersions = announcements.filter((announcement) => {
      return !session.announcements.some((previous) => {
        return (
          previous.id === announcement.id &&
          previous.resetKey === announcement.resetKey
        );
      });
    });
    setSession({
      announcements,
      queue: undismissedInAppAnnouncements({
        announcements: newVersions,
        userId,
        storage,
      }),
    });
  }
  return {
    ...session,
    open: session.queue.length > 0,
    dismiss: (viewed: Announcement[]) => {
      for (const announcement of viewed) {
        try {
          dismissInAppAnnouncement({ announcement, userId, storage });
        } catch (error) {
          // Blocked storage is covered by the server-side interaction row.
          if (!(error instanceof DOMException)) {
            throw error;
          }
        }
      }
      setSession((current) => ({ ...current, queue: [] }));
    },
  };
}
